import { useEffect, useState } from "react";
import {
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
} from "@/components/kit";
import { cn } from "@/lib/utils";
import { isMatchedImportLine } from "@/modules/inventory/domain/import-order-line-filters";
import type { ImportOrderPhase13LineDTO } from "@/modules/inventory/domain/types";

type ShortRow = {
  importOrderLineId: string;
  productName: string;
  variantName: string;
  remaining: number;
  enabled: boolean;
  shortQuantity: number;
  stockUnitLabel: string;
  receiveUnitLabel: string | null;
};

function buildRows(lines: ImportOrderPhase13LineDTO[]): ShortRow[] {
  return lines
    .filter(
      (line) => isMatchedImportLine(line) && line.remainingQuantity > 0,
    )
    .map((line) => ({
      importOrderLineId: line.id,
      productName: line.productName,
      variantName: line.variantName,
      remaining: line.remainingQuantity,
      enabled: false,
      shortQuantity: 0,
      stockUnitLabel: line.stockUnitLabel,
      receiveUnitLabel: line.receiveUnitLabel,
    }));
}

type MarkImportOrderShortDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  lines: ImportOrderPhase13LineDTO[];
  isSubmitting?: boolean;
  onConfirm: (input: {
    note?: string;
    lines: Array<{ importOrderLineId: string; shortQuantity: number }>;
  }) => void | Promise<void>;
};

export function MarkImportOrderShortDialog({
  open,
  onOpenChange,
  lines,
  isSubmitting,
  onConfirm,
}: MarkImportOrderShortDialogProps) {
  const [rows, setRows] = useState<ShortRow[]>(() => buildRows(lines));
  const [note, setNote] = useState("");

  useEffect(() => {
    if (open) {
      setRows(buildRows(lines));
      setNote("");
    }
  }, [open, lines]);

  const canSubmit = rows.some(
    (row) =>
      row.enabled &&
      row.shortQuantity > 0 &&
      row.shortQuantity <= row.remaining,
  );

  function toggleLine(lineId: string, enabled: boolean) {
    setRows((prev) =>
      prev.map((row) =>
        row.importOrderLineId === lineId
          ? {
              ...row,
              enabled,
              shortQuantity: enabled ? row.remaining : 0,
            }
          : row,
      ),
    );
  }

  function setShortQuantity(lineId: string, qty: number) {
    setRows((prev) =>
      prev.map((row) => {
        if (row.importOrderLineId !== lineId) return row;
        const shortQuantity = Math.max(0, Math.min(qty, row.remaining));
        return {
          ...row,
          shortQuantity,
          enabled: shortQuantity > 0,
        };
      }),
    );
  }

  async function handleConfirm() {
    await onConfirm({
      note: note.trim() || undefined,
      lines: rows
        .filter((row) => row.enabled && row.shortQuantity > 0)
        .map((row) => ({
          importOrderLineId: row.importOrderLineId,
          shortQuantity: row.shortQuantity,
        })),
    });
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">
            ບັນທຶກຂອງຂາດ
          </DialogTitle>
        </DialogHeader>
        <p className="text-muted-foreground text-sm">
          ຂອງທີ່ບໍ່ມາຄົບ — ບໍ່ມີຜົນຕໍ່ສະຕັອກ ແຕ່ປິດຈຳນວນຄ້າງ
        </p>
        <Input
          placeholder="ໝາຍເຫດ (ຖ້າມີ)"
          value={note}
          onChange={(e) => setNote(e.target.value)}
        />
        <ul className="flex max-h-64 flex-col gap-2 overflow-y-auto">
          {rows.map((row) => (
            <li
              key={row.importOrderLineId}
              className={cn(
                "rounded-xl border p-3",
                row.enabled ? "border-secondary/30" : "opacity-70",
              )}
            >
              <div className="flex items-start gap-3">
                <Checkbox
                  checked={row.enabled}
                  onCheckedChange={(c) =>
                    toggleLine(row.importOrderLineId, Boolean(c))
                  }
                  className="mt-1"
                />
                <div className="flex-1">
                  <p className="font-medium text-sm">{row.productName}</p>
                  <p className="text-muted-foreground text-xs">
                    ຄ້າງ {row.remaining}{" "}
                    {row.receiveUnitLabel?.trim() || row.stockUnitLabel}
                  </p>
                  <Input
                    type="number"
                    min={0}
                    max={row.remaining}
                    disabled={!row.enabled}
                    value={row.enabled ? row.shortQuantity : 0}
                    onChange={(e) =>
                      setShortQuantity(
                        row.importOrderLineId,
                        Number(e.target.value) || 0,
                      )
                    }
                    className="mt-2 h-9 w-24"
                  />
                </div>
              </div>
            </li>
          ))}
        </ul>
        <DialogFooter>
          <Button
            variant="outline"
            className="rounded-full"
            onClick={() => onOpenChange(false)}
          >
            ຍົກເລີກ
          </Button>
          <Button
            className="rounded-full"
            disabled={!canSubmit}
            isLoading={isSubmitting}
            onClick={handleConfirm}
          >
            ບັນທຶກຂາດ
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
